import { PLAYLIST_FALLBACK_TITLE, type EventPlaylist } from '../lib/content';
import { IconExternal, IconMusic, IconSpotify } from './Icon';

/**
 * The after party playlist, as a row on Now (D57).
 *
 * **A link, not an embed.** Spotify's iframe is playback-only — there is no
 * add-to-playlist control inside it — so an embed would show guests the
 * playlist and give them no way to contribute, which is the entire reason this
 * exists. The link opens the Spotify app, where adding works. It also keeps a
 * third-party frame off a page whose own footer promises nobody else gets your
 * data, and off a bundle tuned for hotel wifi.
 *
 * It shows the playlist's **own** cover, name and description rather than a
 * generic "playlist" label, because that is what a guest is about to land on —
 * and because the description Meg and Videet wrote is already the call to
 * action, better than anything this file would put there instead.
 *
 * Shaped like `TeamStanding`: same row, same height, same tap. Two cards
 * stacked above the hero event card are the ceiling for that space, which is
 * why this one is here only while it can still be acted on — see the window
 * in `Now`.
 */
export default function PlaylistCard({ playlist }: { playlist: EventPlaylist }) {
  return (
    <a
      className="card playlist-row"
      href={playlist.url}
      target="_blank"
      rel="noreferrer noopener"
    >
      {playlist.coverUrl ? (
        <img className="pl-cover" src={playlist.coverUrl} alt="" width={40} height={40} loading="lazy" decoding="async" />
      ) : (
        // No cover yet is a normal state — the link can be pasted in before the
        // artwork is. The mark keeps the row the same height either way.
        <span className="pl-icon" aria-hidden="true"><IconMusic size={18} /></span>
      )}
      <span className="pl-main">
        <span className="pl-title">{playlist.title ?? PLAYLIST_FALLBACK_TITLE}</span>
        <span className="pl-sub">{playlist.description ?? 'Add a song'}</span>
      </span>
      {/* Both, because they say different things: the mark says *where* the tap
          lands, the arrow says you are leaving this app to get there. */}
      <span className="pl-out">
        <IconSpotify size={18} />
        <IconExternal size={14} />
      </span>
    </a>
  );
}
